import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  highlightTextNodes,
  removeNodeHighlights,
} from "../common/utils/addOnServices/highlightTextNodes";
import { EvestmentNote } from "./EvestmentInterface";
import { FileText, Building, Calendar, User, Tag, Info } from "lucide-react";

const EvestmentPageViewer = ({ response }: { response: any }) => {
  const [responseData, setResponseData] = useState<EvestmentNote | null>(null);
  const [content, setContent] = useState<string>("");
  const fileViewerData = useSelector((state: any) => state.fileViewerData);

  useEffect(() => {
    if (response) {
      response.json().then((data: EvestmentNote) => {
        setResponseData(data);
        if (data.body_html) {
          // Use the HTML directly to preserve all formatting
          setContent(data.body_html);
        }
      });
    }
  }, [response]);

  useEffect(() => {
    if (content) {
      highlightTextNodes("#evestment-note-content", fileViewerData);
    }
  }, [fileViewerData, content]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="h-full w-full flex flex-col">
      {/* Header with title and basic info */}
      <div className="w-full border-b bg-white">
        <div className="p-4">
          <div className="flex items-center gap-2 text-gray-600 mb-2">
            <FileText className="w-[18px] h-[18px]" />
            <span className="font-medium">eVestment Note</span>
          </div>
          <div className="text-[24px] font-medium mb-2">
            {responseData?.subject || "Untitled Note"}
          </div>
          <div className="text-[12px] text-gray-400 flex items-center gap-4">
            {responseData?.modified && (
              <span>{formatDate(responseData.modified)}</span>
            )}
            {responseData?.entity_name && (
              <span className="text-blue-600">{responseData.entity_name}</span>
            )}
            {responseData?.note_type_name && (
              <span className="text-gray-500">
                {responseData.note_type_name}
              </span>
            )}
            {responseData?.important && (
              <span className="text-orange-500 font-medium">Important</span>
            )}
            {responseData?.is_meeting && (
              <span className="text-purple-500 font-medium">Meeting</span>
            )}
          </div>
        </div>
      </div>

      {/* Metadata section on top */}
      {responseData && (
        <div className="w-full border-b bg-gray-50 text-[13px]">
          <div className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {/* Entity Information */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-gray-600">
                  <Building className="w-[14px] h-[14px]" />
                  <span className="font-medium">Entity</span>
                </div>
                <div className="font-medium">{responseData.entity_name}</div>
                <div className="text-gray-500 text-xs">
                  {responseData.entity_type}
                </div>
              </div>

              {/* Note Type */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-gray-600">
                  <Tag className="w-[14px] h-[14px]" />
                  <span className="font-medium">Type</span>
                </div>
                <div>{responseData.note_type_name}</div>
              </div>

              {/* As Of Date */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-[14px] h-[14px]" />
                  <span className="font-medium">As Of</span>
                </div>
                <div className="text-xs">{formatDate(responseData.as_of)}</div>
              </div>

              {/* Created */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-gray-600">
                  <User className="w-[14px] h-[14px]" />
                  <span className="font-medium">Created By</span>
                </div>
                <div className="text-xs">{responseData.created_by_name}</div>
                <div className="text-xs text-gray-500">
                  {formatDate(responseData.created)}
                </div>
              </div>

              {/* Modified */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-gray-600">
                  <User className="w-[14px] h-[14px]" />
                  <span className="font-medium">Modified By</span>
                </div>
                <div className="text-xs">{responseData.modified_by_name}</div>
                <div className="text-xs text-gray-500">
                  {formatDate(responseData.modified)}
                </div>
              </div>

              {/* Meeting & Flags */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-gray-600">
                  <Info className="w-[14px] h-[14px]" />
                  <span className="font-medium">Flags</span>
                </div>
                <div className="space-y-1">
                  {responseData.is_meeting && (
                    <div className="text-blue-600 text-xs">
                      Meeting{responseData.is_on_site_meeting && " (On-site)"}
                    </div>
                  )}
                  {responseData.important && (
                    <div className="text-orange-600 text-xs">Important</div>
                  )}
                </div>
              </div>
            </div>

            {/* Mentioned section if it exists */}
            {responseData.mentioned && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <Tag className="w-[14px] h-[14px]" />
                  <span className="font-medium">Mentioned</span>
                </div>
                <div className="text-xs text-gray-600">
                  {responseData.mentioned}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main content area */}
      <div className="flex-1 w-full p-4 overflow-y-auto">
        {content ? (
          <div
            dangerouslySetInnerHTML={{ __html: content }}
            id="evestment-note-content"
            className="max-w-none [&>ul]:list-disc [&>ul]:pl-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>li]:mb-1 [&>p]:mb-4 [&>h1]:text-xl [&>h1]:font-semibold [&>h1]:mb-4 [&>h2]:text-lg [&>h2]:font-semibold [&>h2]:mb-3 [&>h3]:text-base [&>h3]:font-semibold [&>h3]:mb-2"
            onClick={() => {
              removeNodeHighlights("#evestment-note-content");
            }}
          />
        ) : (
          <div className="text-gray-500 text-center py-8">Loading...</div>
        )}
      </div>
    </div>
  );
};

export default EvestmentPageViewer;
